/**
 * GENERATED from source/differential-learning.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const DIFFERENTIAL_LEARNING = {
  "key": "differential-learning-concept",
  "title": "Differential Learning",
  "category": "Concepts",
  "platform": {
    "key": "differential-learning-concept",
    "sub": "Push the Borders. Reorganize the Skill.",
    "duration": "8–10 min read",
    "chapter": {
      "id": "differential-learning",
      "title": "Differential Learning"
    }
  },
  "cert": {
    "key": "differential-learning-cert",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "intro",
      "title": "Differential Learning",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Differential Learning"
        },
        {
          "kind": "text",
          "md": "Differential Learning is deliberately practicing outside your normal movement pattern to discover better or more adaptable solutions.\n\nA slightly more precise definition:"
        },
        {
          "kind": "bigIdea",
          "md": "Differential Learning deliberately increases movement variability, often beyond what normally occurs in competition, so the athlete explores the solution space instead of repeatedly reproducing one preferred technique."
        },
        {
          "kind": "text",
          "md": "That last part matters.\n\nDifferential Learning is not simply:\n\n**Make the drill more variable.**\n\nAnd it is not:\n\n**Do random weird stuff.**\n\nThe athlete is intentionally being pushed away from their normal movement solution.\n\nThe reason we do that can be different depending on what we are trying to accomplish.\n\nAt BAM, we think about two major uses:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Exploration"
        },
        {
          "kind": "text",
          "md": "**Expand the borders of the skill.**"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Recoordination"
        },
        {
          "kind": "text",
          "md": "**Change where the athlete organizes within those borders.**\n\nThose are related, but they are not the same goal."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Where Did Differential Learning Come From?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Differential Learning was proposed by Wolfgang Schöllhorn in the late 1990s as a motor-learning approach built around unusually high amounts of movement variation.\n\nTraditional versions of the approach challenged two common assumptions:\n\n1. that there is one movement pattern that should be reproduced as consistently as possible, and\n2. that improvement primarily comes from repeating invariant parts of that movement.\n\nInstead, Differential Learning deliberately introduces large fluctuations from repetition to repetition.\n\nIn many classical DL protocols:\n\n- movements are changed almost every repetition,\n- exact repetitions are avoided,\n- and corrective technical feedback is minimized or removed.\n\nThe athlete experiences a wide range of movement solutions rather than repeatedly being brought back toward one prescribed model.\n\nThat is the traditional framework.\n\nBAM does **not** believe every one of those rules has to be followed rigidly for Differential Learning to be useful.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Why “Noise” Shows Up in Differential Learning",
          "blocks": [
            {
              "kind": "prose",
              "md": "Differential Learning theory often describes the added variability as **noise** or fluctuations.\n\nThe basic idea is that variability can perturb the athlete's current organization and encourage the movement system to search for different solutions.\n\nThat explanation comes from dynamical-systems and self-organization ideas.\n\nBut be careful with the word **noise**.\n\nIt can make coaches think:\n\n> **Randomness itself is the intervention.**\n\nThat is not how BAM wants to use the concept.\n\nThere is still debate about the exact mechanisms behind Differential Learning, and current research does not establish that simply maximizing random movement variation will maximize learning.\n\nFor us, variability should usually have a purpose."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Simplest Way to Picture It"
        },
        {
          "kind": "text",
          "md": "Imagine the athlete has a movement solution they naturally return to.\n\nDifferential Learning intentionally pushes them away from it.\n\nSometimes we want to show them:\n\n**There are more solutions out here than you currently use.**\n\nOther times we want to show them:\n\n**The solution you keep returning to is not the only place you can organize.**\n\nThat gives us our two main categories."
        }
      ]
    },
    {
      "id": "exploration",
      "title": "1. Exploration",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "1. Exploration"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Expand the Borders of the Skill"
        },
        {
          "kind": "text",
          "md": "Exploration is about increasing the athlete's **action capacity**.\n\nThink of the athlete's current skill as a circle.\n\nEverything inside that circle is a movement solution they can comfortably access.\n\nExploration pushes the edges of that circle outward.\n\nThe goal is not simply to experience unusual movement once.\n\nWe want to make more of those solutions accessible later."
        },
        {
          "kind": "bigIdea",
          "md": "Exploration asks: What else can I do?"
        },
        {
          "kind": "text",
          "md": "In shooting, BAM examples include deliberately changing:\n\n- footwork,\n- staggered stance,\n- release height,\n- release position,\n- ball path,\n- or combinations of those variables.\n\nFor an advanced shooter, we may even manipulate more than one dimension at once.\n\nThe intent is to explore.\n\nWe are deliberately pushing the borders of the skill."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Exploration Is Not the Same as Game Representativeness"
        },
        {
          "kind": "text",
          "md": "Some Differential Learning repetitions may look unlike anything we would intentionally ask an athlete to do in a game.\n\nThat can be okay.\n\nThe purpose of the repetition may not be:\n\n**Practice this exact movement because you will reproduce it in competition.**\n\nIt may be:\n\n**Use this exaggerated movement to expand what your system is capable of organizing.**\n\nThis is where we should think in terms of a continuum:"
        },
        {
          "kind": "framework",
          "steps": [
            {
              "q": "Specific transfer",
              "md": "The variation stays relatively close to solutions that occur in the game."
            },
            {
              "q": "Broader transfer",
              "md": "The variation moves farther from normal game behavior but may still develop useful movement qualities."
            },
            {
              "q": "Essentially a different skill",
              "md": "The variation becomes so different that any transfer is increasingly general rather than specific to the original basketball skill."
            }
          ]
        },
        {
          "kind": "text",
          "md": "There is no magical percentage where the line changes.\n\nThe important question is:"
        },
        {
          "kind": "bigIdea",
          "md": "How directly is this variation still developing the skill we care about?"
        }
      ]
    },
    {
      "id": "recoordination",
      "title": "2. Recoordination",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "2. Recoordination"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Change Where the Athlete Organizes"
        },
        {
          "kind": "text",
          "md": "Recoordination has a different purpose.\n\nSometimes an athlete has become locked into a movement organization that we want to change.\n\nThe movement may be very stable.\n\nThat does not mean it is the best available solution.\n\nInstead of repeatedly telling the athlete:\n\n**Move this here. Put that there.**\n\nwe can deliberately exaggerate the movement away from their normal solution."
        },
        {
          "kind": "bigIdea",
          "md": "Recoordination asks: Can I reorganize how I already do this?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Example: Release Position"
        },
        {
          "kind": "text",
          "md": "Imagine a shooter's release naturally sits farther to the left side of their head than we would like.\n\nOne recoordination strategy would be to deliberately explore exaggerated release locations.\n\nVery far left.\n\nVery far right.\n\nThen return toward more normal shooting.\n\nThe athlete experiences a much wider bandwidth than usual.\n\nThe point is **not** that either extreme is the answer.\n\nThe extremes help destabilize the rigid solution the athlete keeps returning to and expose them to other ways of organizing the shot.\n\nUsually, BAM would explore both sides of the athlete's existing solution.\n\nBut if the athlete is already extremely far toward one edge of the useful movement space, there may be little reason to push even farther in that direction."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Exploration vs. Recoordination"
        },
        {
          "kind": "text",
          "md": "The distinction is simple:"
        },
        {
          "kind": "bigIdea",
          "md": "Exploration expands the space."
        },
        {
          "kind": "bigIdea",
          "md": "Recoordination changes where the athlete organizes inside it."
        },
        {
          "kind": "text",
          "md": "Exploration is about keeping more solutions available.\n\nRecoordination is about using variation to loosen a specific organization and potentially allow a better one to emerge.\n\nThose goals can overlap.\n\nBut knowing which one you are actually trying to accomplish makes the practice much more intentional."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Attractor Idea",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A useful way to think about a stable movement pattern is as an **attractor**.\n\nAn attractor is a state the system tends to return toward.\n\nAn athlete may have many possible ways to move, but over time certain movement organizations become highly stable.\n\nThat stability can be extremely useful.\n\nIt is part of what makes skilled movement reliable.\n\nBut sometimes the athlete becomes too locked into a solution that is limiting them.\n\nDifferential Learning can be used as a perturbation.\n\nWe push the athlete farther from the current attractor than normal practice would.\n\nThe goal is not to permanently live at the extreme.\n\nIt is to make the existing organization less rigid enough that another organization can emerge.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Self-Organization, Degrees of Freedom, and Differential Learning",
          "blocks": [
            {
              "kind": "prose",
              "md": "Humans have many degrees of freedom available when solving a movement problem.\n\nDifferent joints, timings, forces, and positions can combine in multiple ways to achieve the same task.\n\nDifferential Learning deliberately exposes the system to different configurations.\n\nThe proposed benefit is that this can encourage the athlete to self-organize rather than only reproduce a coach-prescribed model.\n\nThat fits with the broader idea of **motor abundance**:\n\nMore available movement options can provide flexibility.\n\nBut that does not mean every option is equally useful.\n\nThe coaching problem is still identifying which parts of the solution space are worth exploring."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Recoordination Usually Needs More Precision Than Exploration"
        },
        {
          "kind": "text",
          "md": "When BAM uses Differential Learning for recoordination, we usually want to manipulate **one important movement dimension at a time**.\n\nWhy?\n\nBecause we are trying to learn something.\n\nIf we change:\n\n- release height,\n- ball path,\n- stance,\n- timing,\n- and balance\n\nall at once, it becomes difficult to know what actually helped.\n\nThe athlete is also dealing with a huge amount of noise.\n\nSo for recoordination:"
        },
        {
          "kind": "bigIdea",
          "md": "Change enough to perturb the pattern, but keep the target clear enough to learn from it."
        },
        {
          "kind": "text",
          "md": "Exploration can sometimes be broader.\n\nEspecially with advanced athletes, there are situations where we intentionally combine variables and let the athlete solve a much larger movement problem."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Differential Learning Does Not Have to Finish the Technical Change"
        },
        {
          "kind": "text",
          "md": "An exaggerated Differential Learning series may help **unlock** a movement.\n\nThat does not mean we expect the athlete to permanently recoordinate after one set.\n\nOften, BAM pairs Differential Learning with more constraint-based technical work.\n\nFor example:\n\nA player has been exploring different ball paths.\n\nAfter exaggerating both sides of that movement space, the coach can create a physical constraint around the path we do not want.\n\nOne BAM example is positioning a hand where the unwanted ball path would travel and asking the athlete not to hit the hand with the ball.\n\nThe differential work loosens the existing organization.\n\nThe constraint then helps stabilize a more useful one."
        },
        {
          "kind": "bigIdea",
          "md": "Unlock the movement. Then help the athlete stabilize the new solution."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Feedback: BAM Does Not Use a Hard “No Feedback” Rule"
        },
        {
          "kind": "text",
          "md": "Traditional Differential Learning often minimizes corrective feedback.\n\nBAM does not treat that as a commandment.\n\nSome athletes benefit from experiencing the variation with very little explanation.\n\nLet them feel the contrast.\n\nLet the movement reorganize.\n\nOther athletes are highly analytical.\n\nFor them, understanding **why** they are being pushed toward different extremes may make the experience more useful.\n\nSo our rule is not:\n\n**Never give feedback.**\n\nIt is:"
        },
        {
          "kind": "bigIdea",
          "md": "Do not over-control the exploration, but explain the purpose when explanation helps that athlete learn."
        },
        {
          "kind": "text",
          "md": "The athlete still needs room to experience the movement rather than turning every repetition into conscious body-part correction."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Traditional Differential Learning vs. BAM's Use",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Classical Differential Learning often emphasizes:\n\n- very high variation,\n- avoiding exact repetition,\n- little or no corrective feedback,\n- and allowing movement solutions to self-organize.\n\nBAM borrows the parts that are useful without treating the framework as ideology.\n\nWe are comfortable:\n\n- giving feedback when it helps,\n- returning to normal repetitions,\n- combining DL with constraints,\n- selecting a specific movement dimension to perturb,\n- and using less variability when stabilization is the bigger need.\n\nThe method serves the athlete.\n\nThe athlete does not serve the method.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "useful",
      "title": "Don't Confuse Weird With Useful",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Don't Confuse Weird With Useful"
        },
        {
          "kind": "text",
          "md": "Differential Learning can look creative.\n\nThat creates a trap.\n\nA drill can be incredibly unusual without having a meaningful relationship to the problem the athlete needs to solve."
        },
        {
          "kind": "bigIdea",
          "md": "Weird is not a training quality."
        },
        {
          "kind": "text",
          "md": "Before adding a differential variation, ask:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "What quality am I expanding?"
        },
        {
          "kind": "text",
          "md": "or:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "What organization am I trying to loosen?"
        },
        {
          "kind": "text",
          "md": "If you cannot answer either question, there is a good chance you are adding novelty rather than designing learning."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Variation Should Usually Have a Target"
        },
        {
          "kind": "text",
          "md": "Imagine a shooter struggles to adapt their **release height**.\n\nThat gives us a reason to deliberately exaggerate high and low releases.\n\nIf the issue is **ball path**, we can exaggerate different ball paths.\n\nIf the issue is the organization of an acceleration and the athlete consistently relies on one shin orientation, we can deliberately exaggerate more horizontal and more vertical positions.\n\nThe exact variation changes.\n\nThe logic stays the same:"
        },
        {
          "kind": "bigIdea",
          "md": "Expand the edges of the quality you actually want the athlete to become more adaptable within."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Where Does Differential Learning Fit in Shooting?"
        },
        {
          "kind": "text",
          "md": "BAM already uses a broader shooting-practice framework.\n\nDifferential Learning shows up most directly in two categories:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Exploration Shooting"
        },
        {
          "kind": "text",
          "md": "Purposefully vary technique to expand the athlete's available solution space."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Recoordination Shooting"
        },
        {
          "kind": "text",
          "md": "Use exaggerated variation to perturb a rigid movement organization and help a new one emerge.\n\nThose are different from:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Groove Shooting"
        },
        {
          "kind": "text",
          "md": "Groove a relatively stable movement pattern."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Anchor Shooting"
        },
        {
          "kind": "text",
          "md": "Change the entry or surrounding movement while challenging the athlete to preserve important parts of the shot."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Stress Shooting"
        },
        {
          "kind": "text",
          "md": "Use stressors such as defense, fatigue, or pressure to challenge the athlete's ability to maintain or reorganize the skill.\n\nDifferential Learning is one tool within the larger shooting-development system.\n\nIt is not the entire system."
        },
        {
          "kind": "masterclassLink",
          "key": "building-better-shooters",
          "title": "Building Better Shooters"
        }
      ]
    },
    {
      "id": "research",
      "title": "What Does the Research Actually Say?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "What Does the Research Actually Say?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Differential Learning has promising evidence.\n\nIt does **not** have evidence strong enough to justify saying:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Differential Learning is the best way to learn movement.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A 2021 systematic review and exploratory meta-analysis included 27 studies containing 31 experiments.\n\nAcross the studies, Differential Learning showed a small average advantage during acquisition and a moderate average advantage in retention compared with other motor-learning methods.\n\nBut there were major limitations:\n\n- high heterogeneity,\n- small samples,\n- low statistical power,\n- high overall risk of bias,\n- and relatively few strong comparisons against other variability-based methods.\n\nThe authors concluded that strong claims about Differential Learning being superior would be premature.\n\nSo the practical takeaway is to treat Differential Learning as a promising tool, not as a universally superior training method.",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Interesting. Useful. Promising. Not magic.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "The 2021 Meta-Analysis",
          "blocks": [
            {
              "kind": "prose",
              "md": "The exploratory meta-analysis reported approximately:\n\n- **d = 0.26** in acquisition,\n- **d = 0.61** in retention,\n\nin favor of Differential Learning across the pooled comparisons.\n\nBut heterogeneity was very high:\n\n- roughly **77%** during acquisition,\n- roughly **79%** for retention.\n\nThat means the study results differed substantially from one another.\n\nEven more importantly for coaches, evidence directly comparing Differential Learning with other variability-based approaches such as contextual interference was scarce and inconclusive.\n\nSo this research does **not** tell us:\n\n**Differential Learning beats every other variable practice method.**\n\nIt tells us that DL deserves serious attention and better research."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Basketball Research: Novices",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Recent basketball research gives us an interesting reason not to assume that the best-looking practice produces the best learning.\n\nOne study of novice basketball players compared several contextual-interference schedules with several Differential Learning schedules.\n\nBlocked practice produced better accuracy during acquisition.\n\nBut the Differential Learning groups showed stronger transfer.\n\nThat is important.\n\nA beginner looking worse while practicing does not necessarily mean they are developing a worse skill.\n\nThe added movement exploration may sometimes create a broader solution base that becomes useful later.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Basketball Research: Retention and Pressure",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Another recent study compared blocked, random, and Differential Learning practice for novice basketball jump shooting.\n\nBlocked practice again produced better accuracy during acquisition.\n\nAt retention, Differential Learning outperformed blocked practice.\n\nUnder induced psychological pressure, the Differential Learning group also outperformed the blocked group.\n\nOne study does not establish a universal rule.\n\nBut it supports an idea worth considering:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "A more variable early learning experience may sometimes sacrifice immediate cleanliness while helping the skill become more robust later.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Basketball Research: Trained Youth Players",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A 2025 study compared Differential shooting training with traditional training in youth basketball players over eight weeks.\n\nDifferential training produced stronger improvements in some shooting outcomes.\n\nHowever:\n\n- both groups improved three-point shooting over time,\n- there was no between-group advantage at retention for three-point shooting,\n- and there was no between-group retention advantage in 1v1 small-sided-game scoring.\n\nThat is a valuable warning.",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Improving a variable shooting test does not automatically mean the improvement will transfer to the entire game.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Differential Learning can help a skill.\n\nIt does not eliminate the need for representative practice.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "who",
      "title": "Who Might Benefit Most?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Who Might Benefit Most?"
        },
        {
          "kind": "text",
          "md": "There is no universal answer.\n\nBAM's current coaching interpretation is:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Beginners"
        },
        {
          "kind": "text",
          "md": "Exploration can be valuable early.\n\nBefore an athlete becomes locked into one rigid pattern, exposing them to a broader movement landscape may help them discover a more adaptable initial solution base.\n\nThey still need stability.\n\nThis is not an argument for making every beginner repetition chaotic."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Intermediates"
        },
        {
          "kind": "text",
          "md": "This group may often need more consolidation.\n\nThey have started finding functional solutions but may not own them strongly enough yet.\n\nToo much variation can interfere with stabilizing those patterns."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Advanced Athletes"
        },
        {
          "kind": "text",
          "md": "Advanced players often have a strong enough anchor to explore farther without completely losing the underlying skill.\n\nThis can make larger perturbations particularly useful.\n\nThis is a BAM coaching model, not a scientific ranking of who “should” use Differential Learning."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Why the Beginner Question Is Interesting",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Some basketball studies with novices support the idea that high-variability learning can create worse immediate practice performance but stronger retention or transfer later.\n\nThat fits BAM's belief that beginners can benefit from exploration.\n\nBut the current evidence is not strong enough to say:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Beginners should always use more Differential Learning than intermediates.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Athlete level is only one variable.\n\nWe also care about:\n\n- the skill,\n- the athlete's current solution,\n- confidence,\n- coordination,\n- the amount of stability they already possess,\n- and what the training is trying to accomplish.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Differential Learning vs. Other Variability"
        },
        {
          "kind": "text",
          "md": "This is worth keeping clear."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Practice Variability"
        },
        {
          "kind": "text",
          "md": "The broad category.\n\nThe coach changes some part of the practice."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Contextual Interference"
        },
        {
          "kind": "text",
          "md": "Different tasks or variations are organized in a way that creates interference between repetitions."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Repetition Without Repetition"
        },
        {
          "kind": "text",
          "md": "The athlete repeatedly solves the same general problem while exact repetitions differ."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Differential Learning"
        },
        {
          "kind": "text",
          "md": "We **deliberately amplify movement differences**, often beyond normal game variation, to drive exploration or recoordination.\n\nThat is why Differential Learning deserves its own concept.\n\nIt is not just:\n\n**Variable practice with a cooler name.**"
        }
      ]
    },
    {
      "id": "activity1",
      "title": "ACTIVITY 1: Exploration or Recoordination?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 1: Exploration or Recoordination?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "For each situation, identify the primary intent.",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "intent-a",
          "prompt": "**A**\n\nA shooter deliberately experiments with different staggered stances, release heights, and release locations to broaden the number of shooting solutions they can access.",
          "options": [
            {
              "id": "exploration",
              "label": "Exploration"
            },
            {
              "id": "recoordination",
              "label": "Recoordination"
            }
          ],
          "discussion": "**Answer: Exploration**\n\nThe goal is to expand the available solution space.",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "intent-b",
          "prompt": "**B**\n\nA shooter has a release that consistently sits too far to one side of the head. The coach deliberately has them exaggerate releases much farther to each side before returning toward more normal shots.",
          "options": [
            {
              "id": "exploration",
              "label": "Exploration"
            },
            {
              "id": "recoordination",
              "label": "Recoordination"
            }
          ],
          "discussion": "**Answer: Recoordination**\n\nThe goal is to perturb a specific rigid organization.",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "intent-c",
          "prompt": "**C**\n\nAn advanced shooter deliberately combines a staggered stance with a higher release during an exploratory shooting series.",
          "options": [
            {
              "id": "exploration",
              "label": "Exploration"
            },
            {
              "id": "recoordination",
              "label": "Recoordination"
            }
          ],
          "discussion": "**Answer: Exploration**\n\nMultiple dimensions can be manipulated because the goal is broad exploration rather than diagnosing one technical variable.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 2: Weird or Useful?",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "weird",
          "title": false,
          "numbered": false,
          "intro": "Choose a Differential Learning variation you currently use.\n\nAnswer:",
          "steps": [
            {
              "id": "skill",
              "q": "1. What skill are you trying to improve?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "quality",
              "q": "2. What specific movement quality are you varying?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "intent",
              "q": "3. Is the goal exploration or recoordination?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "distance",
              "q": "4. How far does this variation move away from the actual skill?",
              "hint": "Place it on the continuum:\n\n**Specific transfer → Broader transfer → Essentially a different skill**",
              "rows": 3
            },
            {
              "id": "why",
              "q": "5. Why is this variation useful?",
              "hint": "If your best answer is:\n\n**Because it is different**\n\nthe activity probably needs a clearer purpose.",
              "rows": 3
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity3",
      "title": "ACTIVITY 3: Recoordinate One Variable",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 3: Recoordinate One Variable",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "recoordinate",
          "title": false,
          "numbered": false,
          "intro": "Choose an athlete with a movement pattern you believe is too rigid or limiting.",
          "steps": [
            {
              "id": "dimension",
              "q": "Step 1",
              "hint": "Identify **one** movement dimension you want to reorganize.\n\nDo not choose several.",
              "rows": 3
            },
            {
              "id": "where",
              "q": "Step 2",
              "hint": "Where does the athlete currently organize within that dimension?",
              "rows": 3
            },
            {
              "id": "edges",
              "q": "Step 3",
              "hint": "What two exaggerated versions could help them experience the opposite edges?\n\nIf the athlete is already at an extreme, explain why you would not push farther in that direction.",
              "rows": 3
            },
            {
              "id": "return",
              "q": "Step 4",
              "hint": "How will you return toward the normal skill?",
              "rows": 3
            },
            {
              "id": "explain",
              "q": "Step 5",
              "hint": "Would you allow the athlete to self-organize first, or give them more explanation?\n\nWhy?",
              "rows": 3
            },
            {
              "id": "constraint",
              "q": "Step 6",
              "hint": "Could a constraint help stabilize the new organization after the differential work?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Use an activity you actually coach.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 4: Design for the Athlete",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "athletes",
          "title": false,
          "numbered": false,
          "intro": "Consider three athletes learning the same general skill:\n\nFor each athlete, decide:\n\n- how much exploration you would use,\n- how much stabilization you would use,\n- whether recoordination is needed,\n- and how extreme you would allow the movement variation to become.\n\nThere is no universal correct answer.\n\nDefend your choices based on the athlete's current needs.",
          "steps": [
            {
              "id": "a",
              "q": "Athlete A",
              "hint": "A beginner with very little stable movement history.",
              "rows": 3
            },
            {
              "id": "b",
              "q": "Athlete B",
              "hint": "An intermediate athlete with a functional solution that still lacks consistency.",
              "rows": 3
            },
            {
              "id": "c",
              "q": "Athlete C",
              "hint": "An advanced athlete with a highly stable solution.",
              "rows": 3
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "map",
      "title": "A Simple Decision Map",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Simple Decision Map"
        },
        {
          "kind": "framework",
          "steps": [
            {
              "q": "DO I WANT TO EXPAND WHAT THE ATHLETE CAN ACCESS?",
              "md": "Yes → Exploration\nPush the borders of the skill outward."
            },
            {
              "q": "IS THE ATHLETE LOCKED INTO A SPECIFIC MOVEMENT ORGANIZATION I WANT TO CHANGE?",
              "md": "Yes → Recoordination\nPerturb that dimension and let the system experience other organizations."
            },
            {
              "q": "DOES THE ATHLETE MOSTLY NEED TO STABILIZE SOMETHING FUNCTIONAL?",
              "md": "Yes → Differential Learning may not be the priority.\nUse more appropriate forms of practice."
            },
            {
              "q": "AM I ADDING VARIABILITY WITHOUT KNOWING WHY?",
              "md": "Yes → Stop.\nDon't confuse weird with useful."
            }
          ]
        },
        {
          "kind": "reflection",
          "md": "Think of one athlete you coach.\n\nIs there a skill where they need to:\n\n**expand the borders of what they can do**\n\nor\n\n**reorganize a solution they are currently stuck in?**\n\nThose are different problems.\n\nWould you train them differently?",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "reflection",
          "title": false,
          "numbered": false,
          "intro": "Choose one skill you coach frequently.\n\nAnswer:",
          "steps": [
            {
              "id": "solutions",
              "q": "What solutions does this athlete already have?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "borders",
              "q": "Where are the borders of their current skill?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "expand",
              "q": "Which border would be useful to expand?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "locked",
              "q": "Are they locked into any solution that needs to be destabilized?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "stability",
              "q": "Where should the practice return to stability afterward?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "transfer",
              "q": "How will you know whether the differential work actually transferred?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The last question matters most.\n\nUnusual practice can look productive.\n\nTransfer tells us whether it was.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway"
        },
        {
          "kind": "text",
          "md": "Differential Learning is not a competition to create the weirdest drill.\n\nIt is a way of deliberately pushing athletes outside their normal movement solution.\n\nSometimes we do that to:"
        },
        {
          "kind": "bigIdea",
          "md": "expand what they can access."
        },
        {
          "kind": "text",
          "md": "Sometimes we do it to:"
        },
        {
          "kind": "bigIdea",
          "md": "help a rigid movement reorganize."
        },
        {
          "kind": "text",
          "md": "At BAM:"
        },
        {
          "kind": "bigIdea",
          "md": "Exploration expands the space."
        },
        {
          "kind": "bigIdea",
          "md": "Recoordination changes where the athlete organizes inside it."
        },
        {
          "kind": "text",
          "md": "The farther we move away from the actual skill, the more our expected transfer shifts from specific toward general.\n\nAnd sometimes the athlete does not need more variability at all.\n\nThey need stability.\n\nThe coaching skill is knowing the difference."
        },
        {
          "kind": "bigIdea",
          "md": "Push the borders. Reorganize when needed. Don't confuse weird with useful."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Suggested References",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "- Tassignon, B., Verschueren, J., Baeyens, J.-P., et al. (2021). An Exploratory Meta-Analytic Review on the Empirical Evidence of Differential Learning as an Enhanced Motor Learning Method. Frontiers in Psychology, 12, 533033.\n- Schöllhorn, W. I. — foundational Differential Learning work beginning in 1999 and subsequent theoretical/applied work on movement fluctuations and variability.\n- Effect of Contextual Interference and Differential Learning on Motor Skill Development and Motivation in Novice Basketball Players — novice basketball jump-shooting study comparing multiple contextual-interference and DL schedules.\n- Challenge Under Pressure: Contextual Interference or Differential Learning — novice basketball jump-shot acquisition, retention, and pressure-performance comparison.\n- Differential shooting training in youth basketball players: an analysis of performance effects (2025) — eight-week differential vs. traditional shooting intervention in trained youth players.",
          "depth": "cert"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            },
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            }
          ]
        }
      ]
    }
  ]
};
